import { AlertCircle, Loader2, RotateCw } from 'lucide-react';

/**
 * A panel that could not load, saying something worth reading.
 *
 * ## The standard this is holding to
 *
 * The Power screen already does this properly. When a forwarded message
 * cannot be read it says what failed, why it probably failed, and what to do:
 * *"not recognised (no 20-digit token, no M-Pesa receipt code, no amount).
 * Forward the real M-Pesa confirmation…"*. Somebody who reads that knows their
 * next move.
 *
 * Elsewhere the same situation said **"Unavailable."** — a fact, full stop,
 * with nothing to act on and no way to try again without reloading the page.
 * This exists so the rest of the application can reach the standard the Power
 * screen already set, rather than each panel inventing its own half of it.
 *
 * ## The three things, and why each
 *
 * **What failed** names the panel, because a screen has several and an error
 * floating between them belongs to none.
 *
 * **The likely cause** is a guess, and is worded as one. A confident wrong
 * diagnosis sends somebody to check the wrong thing for twenty minutes; a
 * hedged right one gets checked in two.
 *
 * **One thing to do** is one, not a list. A list is what somebody writes when
 * they do not know which it is, and it reads as such.
 *
 * Retry is a control rather than advice to reload, because reloading loses
 * everything else on the screen that did load.
 */
export function PanelError({
  what,
  cause,
  fix,
  detail,
  onRetry,
  retrying = false,
}: {
  /** The panel that failed, named: "System status could not be read". */
  what: string;
  /** The most likely reason, hedged honestly. */
  cause: string;
  /** The single next action. */
  fix: string;
  /** The underlying message, for the person who wants it. Optional. */
  detail?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="rounded-xl border border-danger-300 bg-danger-50 p-4 text-sm text-danger-700">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">{what}</p>
          <p className="mt-1">{cause}</p>
          <p className="mt-1">{fix}</p>

          {/*
            The raw message kept, and kept quiet. Somebody debugging wants the
            exact words; everybody else is helped by the three lines above and
            actively confused by a stack of provider jargon.
          */}
          {detail && <p className="mt-2 break-words text-xs opacity-80">{detail}</p>}

          {onRetry && (
            <button
              type="button"
              className="btn-quiet mt-3 px-3 py-1 text-xs"
              disabled={retrying}
              onClick={onRetry}
              title="Ask again, without losing the rest of this page"
            >
              {retrying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
